import React, { useEffect, useState } from 'react';

import { Button, Grid, makeStyles } from '@material-ui/core';

import CaseActionsArchiveButton from './CaseActionsArchiveButton';
import CaseActionsArchivedAction from './CaseActionsArchivedAction';
import CaseActionsClaim from './CaseActionsClaim';
import CaseActionsClinicUpload from './CaseActionsClinicUpload';
import CaseActionsComplete from './CaseActionsComplete';
import CaseActionsReportInformationAssigned from './CaseActionsReportInformationAssigned';
import CaseActionsReportInformationOthers from './CaseActionsReportInformationOthers';
import CaseActionsReportInformationSubmitted from './CaseActionsReportInformationSubmitted';
import CaseActionsSharedOnlinePortalAndMail from './CaseActionsSharedOnlinePortalAndMail';
import { WorkspaceProductType } from '../../models/Workspace';

/* eslint-disable jsx-a11y/media-has-caption */

const useStyles = makeStyles((theme) => {
  return {
    greenBtn: {
      '&:hover': {
        backgroundColor: '#20794F',
      },
      backgroundColor: '#20794F',
    },
    link: {
      color: theme.palette.primary.main,
    },
    orangeBtn: {
      '&:hover': {
        backgroundColor: '#E2A74F',
      },
      backgroundColor: '#E2A74F',
    },
    purpleBtn: {
      '&:hover': {
        backgroundColor: '#787FF6',
      },
      backgroundColor: '#787FF6',
    },
    redOutlinedBtn: {
      '&:hover': {
        background: 'white',
        borderColor: '#B12A2A',
      },
      borderColor: '#B12A2A',
      color: '#B12A2A',
    },
  };
});

const buttonsMap = [
  {
    callBackAction: 'cancelReview',
    class: 'redOutlinedBtn',
    id: 'caseActions-button-cancelReview',
    shouldAppear: (caseStatus) => caseStatus.status === 'in-review' && caseStatus.isAssignedReviewer,
    text: 'Cancel Review',
    variant: 'outlined',
  },
  {
    callBackAction: 'discard',
    class: 'redOutlinedBtn',
    id: 'button-discard',
    shouldAppear: (caseStatus) => caseStatus.status === 'in-progress' && caseStatus.isAssignedUser,
    text: 'Discard',
    variant: 'outlined',
  },
  {
    callBackAction: 'editVideo',
    class: 'orangeBtn',
    id: 'caseActions-button-editVideo',
    shouldAppear: (caseStatus) =>
      ((caseStatus.status === 'in-progress' || caseStatus.status === 'review') && caseStatus.isAssignedUser) ||
      (caseStatus.status === 'in-review' && caseStatus.isAssignedReviewer),
    text: 'Edit Draft Video',
    variant: 'contained',
  },
  {
    callBackAction: 'startReview',
    class: 'purpleBtn',
    id: 'caseActions-button-startReview',
    shouldAppear: (caseStatus) => caseStatus.status === 'review' && caseStatus.isAdmin && !caseStatus.isAssignedUser,
    text: 'Start Review',
    variant: 'contained',
  },
  {
    callBackAction: 'sendReview',
    class: 'purpleBtn',
    id: 'caseActions-button-sendReview',
    shouldAppear: (caseStatus) => caseStatus.status === 'in-progress' && caseStatus.isAssignedUser,
    text: 'Send for Review',
    variant: 'contained',
  },
  {
    callBackAction: 'signoff',
    class: 'greenBtn',
    id: 'caseActions-button-signoff',
    shouldAppear: (caseStatus) =>
      ((caseStatus.status === 'in-progress' || caseStatus.status === 'review') && caseStatus.isAssignedUser) ||
      (caseStatus.status === 'in-review' && caseStatus.isAssignedReviewer),
    text: 'Sign off Case',
    variant: 'contained',
  },
];

const CaseActions = ({
  auth,
  onCaseAction,
  caseStatus,
  onViewMaterial,
  fileStatus = {},
  onRemoveReport,
  onRemoveFiles,
  onChangeTitle,
  onReportUpload,
  onFilesUpload,
  onSelectSharedImage,
  onRemoveSharedImage,
  onViewSharedImage,
  onGiveOnlinePortalAccess,
  hideFileControls = false,
  loadingSharedImageDetail,
  isSubmitting = false,
  isSubmitting$,
  isArchiving = false,
  isArchiving$,
  isBypassing$,
  caseStudy,
  workspace,
  uploadSharedInfo,
  workspaceMainFileType,
  onOpenImageSelectionDialog,
  radiologyReportRequired,
  onOpenRadiologyReportDialog,
}) => {
  const classes = useStyles();
  const [uploadedReport, setUploadedReport] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState(null);

  const handleReportChange = (evt) => {
    setUploadedReport([...evt.target.files]);
    // eslint-disable-next-line no-param-reassign
    evt.target.value = null;
  };

  const handleFileChange = (evt) => {
    setUploadedFiles([...evt.target.files]);
    // eslint-disable-next-line no-param-reassign
    evt.target.value = null;
  };

  const handleSelectSharedImage = () => {
    if (!onSelectSharedImage || typeof onSelectSharedImage !== 'function') return;
    onSelectSharedImage();
  };

  useEffect(() => {
    if (uploadedReport) {
      onReportUpload(uploadedReport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedReport]);

  useEffect(() => {
    if (uploadedFiles) {
      onFilesUpload(uploadedFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles]);

  const renderArchiveButton = ({ fullWidth = false, size = 'large' } = {}) => {
    return <CaseActionsArchiveButton fullWidth={fullWidth} size={size} auth={auth} isArchiving$={isArchiving$} onCaseAction={onCaseAction} />;
  };

  const renderArchivedAction = () => <CaseActionsArchivedAction onCaseAction={onCaseAction} auth={auth} isArchiving$={isArchiving$} />;

  const renderClaimAction = () => <CaseActionsClaim onCaseAction={onCaseAction} auth={auth} isArchiving$={isArchiving$} isArchiving={isArchiving} />;

  const renderCompleteAction = () => <CaseActionsComplete onCaseAction={onCaseAction} auth={auth} isArchiving$={isArchiving$} />;

  const renderReportInformationNotSubmitted = () => {
    return caseStatus.isAssignedUser ? renderReportInformationAssigned() : renderReportInformationOthers();
  };

  const renderReportInformationSubmitted = () => {
    return <CaseActionsReportInformationSubmitted caseStatus={caseStatus} renderActionButtons={renderActionButtons} onViewMaterial={onViewMaterial} workspace={workspace} />;
  };

  const renderReportInformationOthers = () => <CaseActionsReportInformationOthers caseStatus={caseStatus} onCaseAction={onCaseAction} auth={auth} isArchiving$={isArchiving$} />;

  const renderReportInformationAssigned = () => (
    <CaseActionsReportInformationAssigned caseStatus={caseStatus} onCaseAction={onCaseAction} auth={auth} isArchiving$={isArchiving$} />
  );

  const renderActionButtons = () => {
    const buttonList = buttonsMap.map((button) => {
      if (button.shouldAppear(caseStatus)) {
        return (
          <Grid key={button.id} item>
            <Button
              style={{ marginRight: '0.5rem', marginTop: '0.5rem' }}
              classes={{
                [button.variant === 'outlined' ? 'outlinedPrimary' : 'containedPrimary']: classes[button.class],
              }}
              variant={button.variant}
              color="primary"
              size="medium"
              onClick={() => onCaseAction(button.callBackAction)}>
              {button.text}
            </Button>
          </Grid>
        );
      }
      return null;
    });

    return workspace?.productType !== WorkspaceProductType.LawFirm.value ? (
      <Grid container justify="flex-end" style={{ transform: 'translateX(0.5rem)' }}>
        {buttonList}
        <Grid item>
          <div style={{ marginRight: '0.5rem', marginTop: '0.5rem' }}>{renderArchiveButton({ size: 'medium' })}</div>
        </Grid>
      </Grid>
    ) : null;
  };

  const renderClinicUploadAction = () => {
    return (
      <CaseActionsClinicUpload
        fileStatus={fileStatus}
        onCaseAction={onCaseAction}
        auth={auth}
        isSubmitting={isSubmitting}
        isSubmitting$={isSubmitting$}
        caseStatus={caseStatus}
        hideFileControls={hideFileControls}
        onChangeTitle={onChangeTitle}
        onRemoveFiles={onRemoveFiles}
        onFileChange={handleFileChange}
        onRemoveSharedImage={onRemoveSharedImage}
        loadingSharedImageDetail={loadingSharedImageDetail}
        onSelectSharedImage={handleSelectSharedImage}
        onViewSharedImage={onViewSharedImage}
        onRemoveReport={onRemoveReport}
        onReportChange={handleReportChange}
        isArchiving$={isArchiving$}
        isWhiteGloveWorkspace={workspace?.productType === WorkspaceProductType.whiteGlove.value}
        onGiveOnlinePortalAccess={onGiveOnlinePortalAccess}
        uploadSharedInfo={uploadSharedInfo}
        workspaceMainFileType={workspaceMainFileType}
        onOpenImageSelectionDialog={onOpenImageSelectionDialog}
        radiologyReportRequired={radiologyReportRequired}
        onOpenRadiologyReportDialog={onOpenRadiologyReportDialog}
      />
    );
  };

  const renderUploadSharedOnlinePortalAndMailAction = () => {
    return (
      <CaseActionsSharedOnlinePortalAndMail
        fileStatus={fileStatus}
        onCaseAction={onCaseAction}
        auth={auth}
        isSubmitting={isSubmitting}
        isSubmitting$={isSubmitting$}
        hideFileControls={hideFileControls}
        onChangeTitle={onChangeTitle}
        onRemoveFiles={onRemoveFiles}
        onFileChange={handleFileChange}
        isArchiving$={isArchiving$}
        onRemoveReport={onRemoveReport}
        onReportChange={handleReportChange}
        caseStudy={caseStudy}
        onOpenRadiologyReportDialog={onOpenRadiologyReportDialog}
        isBypassing$={isBypassing$}
        workspace={workspace}
        workspaceMainFileType={workspaceMainFileType}
        onOpenImageSelectionDialog={onOpenImageSelectionDialog}
      />
    );
  };

  if (caseStatus.status === 'upload-shared' || caseStatus.status === 'pending-images') return renderUploadSharedOnlinePortalAndMailAction();
  if (caseStatus.status === 'archived') return renderArchivedAction();
  if (caseStatus.status === 'new' || caseStatus.status === 'delayed') return renderClaimAction();
  if (caseStatus.status === 'in-progress' && !caseStatus.reportSubmitted) return renderReportInformationNotSubmitted();
  if (caseStatus.status === 'incomplete') return renderCompleteAction();
  if (caseStatus.status === 'pending-upload') return renderClinicUploadAction();
  return renderReportInformationSubmitted();
};

export default CaseActions;
